import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { CartException, CartExceptionType, Exception, PaymentResponse, SaleStatus } from '@cargos/sprintpay-models';
import { catchError, debounceTime, filter, finalize, Subject, switchMap, take, takeUntil, throwError } from 'rxjs';
import { InformationCollectorByPaymentService } from 'src/app/shared/modules/information-collector-by-payment/collector/services/information-collector-by-payment.service';
import { PaymentCartService, ResetInformationService, SessionService, SummaryService } from 'src/app/shared/services';
import { FeatureRestrictionsService } from 'src/app/shared/services/features/feature-restrictions.service';
import Swal from 'sweetalert2';
import { CreditCardVerificationService } from '../credit-card-verification-dialog';

@Component({
    selector: 'app-lib-pay-button',
    templateUrl: './pay-button.component.html',
    standalone: true,
    imports: [MatIconModule, MatProgressSpinnerModule, MatButtonModule, MatTooltipModule],
})
export class PayButtonComponent implements OnInit, OnDestroy {
    public isPaying: boolean = false;
    public isGuest: boolean = false;
    private readonly unsubscribe$ = new Subject<void>();
    public tooltipMessage = '';
    public isAllowedToMakeAPayment: boolean = false;

    @Output() eventSubmitPayment = new EventEmitter<string>();
    @Input() disabled: boolean = false;

    constructor(
        private readonly sessionService: SessionService,
        private readonly paymentCartService: PaymentCartService,
        private readonly summaryService: SummaryService,
        private readonly informationCollectorByPaymentService: InformationCollectorByPaymentService,
        private readonly resetInformationService: ResetInformationService,
        private readonly router: Router,
        private readonly featureRestrictionsService: FeatureRestrictionsService,
        private readonly creditCardVerificationService: CreditCardVerificationService
    ) {}

    ngOnInit(): void {
        this.subscribeToUserAuthenticated();
        this.subscribePaymentValidation();
        this.subscribeIfIsAccountOnHold();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    public validateAndPay(): void {
        if (this.isPaying) {
            return;
        }

        this.isPaying = true;
        let failed = false;
        this.eventSubmitPayment.emit();

        this.creditCardVerificationService
            .verifyCreditCardIfNeeded$()
            .pipe(
                take(1),
                filter((verified: boolean) => {
                    if (!verified) {
                        this.isPaying = false;
                    }
                    return verified;
                }),
                switchMap(() => this.paymentCartService.isAllowedToMakeAPayment()),
                take(1),
                debounceTime(300),
                switchMap(() => {
                    return this.paymentCartService.payCart().pipe(
                        take(1),
                        catchError((e) => {
                            failed = true;
                            return throwError(() => {
                                return e;
                            });
                        }),
                        finalize(() => {
                            if (!failed) {
                                this.resetInformationService.resetUserInformation();
                            }
                        })
                    );
                }),
                finalize(() => (this.isPaying = false))
            )
            .subscribe({
                next: (response) => {
                    this.isPaying = false;
                    this.onPaymentComplete(response);
                },
                error: (cartExceptions: CartException[]) => {
                    this.isPaying = false;
                    this.onPaymentError(cartExceptions);
                },
            });
    }

    public onPaymentError(cartExceptions: CartException[]): void {
        if (cartExceptions instanceof Exception) {
            Swal.fire({
                icon: 'error',
                title: cartExceptions?.title,
                html: cartExceptions?.description,
                confirmButtonText: 'Close',
            });
            return;
        }
        const cartExceptionsArray = Array.isArray(cartExceptions) ? cartExceptions : [];
        cartExceptionsArray?.forEach((cartException: CartException) => {
            if (cartException.code === CartExceptionType.TERMS_OF_USE_INVALID) {
                this.markTermsOfUseInvalid();
            }
            if (cartException.code === CartExceptionType.CUSTOMER_REFERENCE_INVALID) {
                this.markCustomerReferenceInvalid();
            }
            if (cartException.code === CartExceptionType.COMPANY_POLICY) {
                this.markNotesInvalid();
            }
        });
    }

    private markCustomerReferenceInvalid(): void {
        this.informationCollectorByPaymentService.markFormAsTouched().pipe(take(1)).subscribe();
    }

    private markTermsOfUseInvalid(): void {
        this.summaryService.markTermsOfUseAsTouched$().pipe(take(1)).subscribe();
    }

    private markNotesInvalid(): void {
        this.summaryService.markNotesAsTouched$().pipe(take(1)).subscribe();
    }

    private subscribeToUserAuthenticated(): void {
        this.sessionService
            .isAuthenticated$()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((isAuthenticated: boolean) => {
                this.isGuest = !isAuthenticated;
            });
    }

    private subscribePaymentValidation(): void {
        this.paymentCartService
            .isAllowedToMakeAPayment$()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (isAllowedToMakeAPayment) => {
                    this.isAllowedToMakeAPayment = isAllowedToMakeAPayment;
                },
            });
    }

    private subscribeIfIsAccountOnHold(): void {
        this.featureRestrictionsService
            .isAccountOnHold()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (isAccountOnHold) => {
                    this.tooltipMessage = isAccountOnHold
                        ? 'You have past due invoices and your account has been placed on a credit hold. Please contact collections@cargosprint.com for support.'
                        : '';
                },
            });
    }

    private onPaymentComplete(cartPayResponses: PaymentResponse[]): void {
        const successfulPayments = cartPayResponses.filter(
            (payResult) => payResult.saleStatus === SaleStatus.SALE_SUCCESS
        );

        if (successfulPayments.length) {
            this.router.navigate(['/cart/confirmation']);
        }
    }
}
